import parsePhoneNumberFromString, { CountryCode } from 'libphonenumber-js';
import { TrackingActions, TrackingCategory } from '@buy-viasat/redux/src/analytics';
import {
  appActions,
  Country,
  CustomerType,
  Modals,
  selectCountry,
  selectCustomerType,
  selectPartyId,
  selectRelnId,
} from '@buy-viasat/redux/src/app';
import {
  addressActions,
  AddressFieldStateType,
  AddressType,
  applyUnsanitizedAddressLines,
  hasAddressBeenChanged,
  selectBillingAddress,
  selectBillingAddressValues,
  selectBillingEqualsServiceAddress,
  selectIsBillingAddressValid,
  selectIsShippingAddressValid,
  selectPreviousBillingAddressValues,
  selectPreviousShippingAddressValues,
  selectShippingAddress,
  selectShippingAddressValues,
  selectShippingEqualsServiceAddress,
  selectUnsanitizedBillingAddressLines,
  selectUnsanitizedShippingAddressLines,
} from '@buy-viasat/redux/src/address';
import { navActions, Routes } from '@buy-viasat/redux/src/navigator';
import { selectCustomerInformation, selectPhoneCountryCode } from '@buy-viasat/redux/src/personal-information';
import {
  selectIsVermontCustomer,
  selectServiceAddressValues,
  selectUnsanitizedAddressLines,
} from '@buy-viasat/redux/src/serviceability';
import {
  CreateIndividualInput,
  CreateOrganizationInput,
  Customer,
  FeatureFlags,
  ProcessStatus,
  SmbCustomer,
  UpdateOrganizationInput,
} from '@buy-viasat/types/build/bv';
import { PayloadAction } from '@reduxjs/toolkit';
import { call, CallEffect, put, PutEffect, select, SelectEffect, takeLatest } from 'redux-saga/effects';
import { TrackingLabel } from 'shared/containers/Analytics/types';
import { benchmark } from 'shared/containers/App/saga';
import scrubAddressAsync from 'shared/providers/requests/fulfillment/scrubAddress';
import createIndividualAsync, { CreateIndividualResponse } from '../../providers/requests/customer/createIndividual';
import {
  createIndividualRest,
  CreateIndividualRestResponse,
} from '../../providers/requests/customer/createIndividualRest';
import updateIndividualAsync, { UpdateIndividualPayload } from '../../providers/requests/customer/updateIndividual';
import { ErrorPayload, PartyId } from '../../types';
import { areAddressesEqual } from '../../utils';
import clientLogger from '../../utils/clientLogger';
import { creditVerificationActions } from '../CreditVerification/slice';
import { selectPersonalInformationValidation } from './selectors';
import { personalInformationActions } from './slice';
import { CreateCustomerPartyAndRelnId, CustomerInfo, MarketoRecordTypes, NavigationGeneratorAction } from './types';
import updateMarketingPreferencesAsync from 'shared/providers/requests/customer/updateMarketingPreferences';
import {
  businessInformationActions,
  selectAccountHolderInformation,
  selectBusinessInformationName,
  selectBusinessInformationSSN,
  selectBusinessInformationTaxID,
  selectIsTaxExempt,
  selectOrganizationPartyId,
  selectShouldUseTaxId,
} from '@buy-viasat/redux/src/business-information';
import createOrganizationAsync, {
  CreateOrganizationResponse,
} from 'shared/providers/requests/customer/createOrganization';
import {
  createOrganizationRest,
  CreateOrganizationRestInput,
  CreateOrganizationRestResponse,
} from 'shared/providers/requests/customer/createOrganizationRest';
import updateOrganizationAsync, {
  UpdateOrganizationPayload,
} from 'shared/providers/requests/customer/updateOrganization';
import { paymentInformationActions } from '@buy-viasat/redux/src/payment-information';
import {
  selectAvailableProducts,
  selectEstimatedServiceableDate,
  selectIsUpsertPlanLoading,
  selectPlanName,
  selectProductFamily,
} from '@buy-viasat/redux/src/plan';
import { sendGenericRetargetingDataAsync } from 'shared/providers/requests/customer/sendGenericRetargetingData';
import { Product } from '@buy-viasat/types/src/bv';
import { selectMonthlyTotalWithPromo } from '@buy-viasat/redux/src/cart';
import { VERMONT } from 'shared/constants';
import { sendFiberRetargetingDataAsync } from 'shared/providers/requests/customer/sendFiberRetargetingData';
import { isFiber, ProductFamily } from '@buy-viasat/utils';
import { USE_NEW_API } from 'shared/utils/dataFetchingHooks/constants';
import { updateOrganizationRest } from 'shared/providers/requests/customer/updateOrganizationRest';

export function* navigationGenerator(
  action: PayloadAction<NavigationGeneratorAction>,
): Generator<PutEffect | CallEffect | SelectEffect, void, void> {
  const billingEqualsService: any = yield select(selectBillingEqualsServiceAddress);
  const isUpsertPlanLoading: any = yield select(selectIsUpsertPlanLoading);
  const customerType: any = yield select(selectCustomerType);
  const isBusinessUser = customerType === CustomerType.B;
  const isVermontCustomer: any = yield select(selectIsVermontCustomer);

  // With the new REST API the credit check is performed server-side (during get-product /
  // add-cart-items), so the dedicated client credit-check call is skipped. We still need to
  // mark it complete so the cart price details (e.g. Due Today fees) become visible.
  if (USE_NEW_API && !isBusinessUser && isVermontCustomer) {
    yield put(creditVerificationActions.setCreditCheckComplete(true));
  }

  if (
    !USE_NEW_API &&
    !isBusinessUser &&
    (action.payload.billingAddress?.region === VERMONT ||
      (action.payload.serviceAddress.region === VERMONT && billingEqualsService))
  ) {
    yield put(creditVerificationActions.checkCustomerCredit());
    yield put(navActions.next());
  } else if (!isUpsertPlanLoading) {
    yield put(navActions.next());
  } else {
    yield put(personalInformationActions.setIsPersonalInformationRequestComplete(true));
  }
}

export function* createCustomerSaga(
  action: PayloadAction<CreateIndividualInput>,
): Generator<
  CallEffect | PutEffect | SelectEffect,
  CreateCustomerPartyAndRelnId,
  CreateIndividualResponse & CreateIndividualRestResponse & FeatureFlags & Country & string
> {
  try {
    const serviceAddress: any = yield select(selectServiceAddressValues);
    const billingAddress: any = yield select(selectBillingAddressValues);
    const shippingAddress: any = yield select(selectShippingAddressValues);
    const planProductFamily: string = yield select(selectProductFamily);
    const hasFiberPlan = isFiber(planProductFamily);

    yield put(personalInformationActions.setIsPersonalInformationLoading(true));
    let partyId: string;
    let relnId: string;

    // TODO: migrate this saga-driven REST call to the tanstack-query hook pattern in a follow-up
    // (batched with the other saga-driven customer API calls).
    if (USE_NEW_API) {
      const country: Country = yield select(selectCountry);
      const created: CreateIndividualRestResponse = yield call(createIndividualRest, {
        ...action.payload,
        country,
      });
      partyId = created.partyId;
      relnId = created.relnId;
    } else {
      const { data } = yield call(createIndividualAsync, action.payload);

      if (!data) {
        throw new Error('No data returned from IRA - createIndividual');
      }
      const response = data as { createIndividual: Customer };
      partyId = response.createIndividual.partyId;
      relnId = response.createIndividual.relnId;
    }

    yield put(appActions.setPartyAndRelnId({ partyId, relnId }));

    if (hasFiberPlan) {
      yield put(personalInformationActions.sendFiberRetargetingData());
    }
    yield put(personalInformationActions.sendGenericRetargetingData());
    yield put(personalInformationActions.setMarketPreferences());
    yield put(personalInformationActions.navigationGenerator({ billingAddress, serviceAddress, shippingAddress }));
    return { partyId, relnId };
  } catch (err: any) {
    yield put(personalInformationActions.onSagaError({ callingSaga: 'createCustomerSaga', err, payload: null }));
    return { partyId: '', relnId: '' };
  }
}

export function* createOrganizationSaga(
  action: PayloadAction<CreateOrganizationInput>,
): Generator<
  CallEffect | PutEffect | SelectEffect,
  CreateCustomerPartyAndRelnId,
  CreateOrganizationResponse & CreateOrganizationRestResponse & FeatureFlags & Country & string
> {
  try {
    const serviceAddress: any = yield select(selectServiceAddressValues);
    const billingAddress: any = yield select(selectBillingAddressValues);
    const shippingAddress: any = yield select(selectShippingAddressValues);
    const planProductFamily: string = yield select(selectProductFamily);
    const hasFiberPlan = isFiber(planProductFamily);

    yield put(personalInformationActions.setIsPersonalInformationLoading(true));

    let partyId: string;
    let relnId: string;
    let organizationPartyId: string;

    // TODO: migrate this saga-driven REST call to the tanstack-query hook pattern in a follow-up
    // (batched with the other saga-driven customer API calls).
    if (USE_NEW_API) {
      const country: Country = yield select(selectCountry);
      const { taxId, isTaxIdSsn, isTaxExempt, ...restPayload } = action.payload;
      const restInput: CreateOrganizationRestInput = {
        ...restPayload,
        // taxId value determined in createOrUpdateCustomerSaga, either EIN or SSN
        taxId: !isTaxIdSsn && taxId ? taxId : undefined,
        ssn: isTaxIdSsn && taxId ? taxId : undefined,
        country,
      };
      const created: CreateOrganizationRestResponse = yield call(createOrganizationRest, restInput);
      partyId = created.individualPartyId;
      relnId = created.relnId;
      organizationPartyId = created.organizationPartyId;
    } else {
      const { data } = yield call(createOrganizationAsync, action.payload);

      if (!data) {
        throw new Error('No data returned from IRA - createOrganization');
      }

      const response = data as { createOrganization: SmbCustomer };
      partyId = response.createOrganization.individualPartyId;
      relnId = response.createOrganization.relnId;
      organizationPartyId = response.createOrganization.organizationPartyId;
    }

    yield put(appActions.setPartyAndRelnId({ partyId, relnId }));
    yield put(appActions.setOrganizationPartyId(organizationPartyId));
    yield put(businessInformationActions.setOrganizationPartyId(organizationPartyId));
    if (hasFiberPlan) {
      yield put(personalInformationActions.sendFiberRetargetingData());
    }
    yield put(personalInformationActions.sendGenericRetargetingData());
    yield put(personalInformationActions.setMarketPreferences());
    yield put(personalInformationActions.navigationGenerator({ billingAddress, serviceAddress, shippingAddress }));
    return { partyId, relnId };
  } catch (err: any) {
    yield put(personalInformationActions.onSagaError({ callingSaga: 'createOrganizationSaga', err, payload: null }));
    return { partyId: '', relnId: '' };
  }
}

export function* updateCustomerSaga(
  action: PayloadAction<CreateIndividualInput & PartyId>,
): Generator<CallEffect | PutEffect | SelectEffect, void, UpdateIndividualPayload> {
  try {
    const serviceAddress: any = yield select(selectServiceAddressValues);
    const billingAddress: any = yield select(selectBillingAddressValues);
    const shippingAddress: any = yield select(selectShippingAddressValues);
    yield put(personalInformationActions.setIsPersonalInformationLoading(true));
    const { data } = yield call(updateIndividualAsync, action.payload);

    yield put(creditVerificationActions.updateCreditVerification(data?.updateIndividual.updateCreditCheck ?? false));
    yield put(personalInformationActions.sendGenericRetargetingData());
    yield put(personalInformationActions.setMarketPreferences());
    yield put(personalInformationActions.navigationGenerator({ billingAddress, serviceAddress, shippingAddress }));
  } catch (err: any) {
    yield put(personalInformationActions.onSagaError({ callingSaga: 'updateCustomerSaga', err, payload: null }));
  }
}

export function* updateOrganizationSaga(
  action: PayloadAction<UpdateOrganizationInput>,
): Generator<CallEffect | PutEffect | SelectEffect, void, UpdateOrganizationPayload & string> {
  try {
    const serviceAddress: any = yield select(selectServiceAddressValues);
    const billingAddress: any = yield select(selectBillingAddressValues);
    const shippingAddress: any = yield select(selectShippingAddressValues);
    const planProductFamily: string = yield select(selectProductFamily);
    const hasFiberPlan = isFiber(planProductFamily);
    yield put(personalInformationActions.setIsPersonalInformationLoading(true));
    yield call(updateOrganizationAsync, action.payload);

    if (hasFiberPlan) {
      yield put(personalInformationActions.sendFiberRetargetingData());
    }
    yield put(personalInformationActions.sendGenericRetargetingData());
    yield put(personalInformationActions.setMarketPreferences());
    yield put(personalInformationActions.navigationGenerator({ billingAddress, serviceAddress, shippingAddress }));
  } catch (err: any) {
    yield put(personalInformationActions.onSagaError({ callingSaga: 'updateOrganizationSaga', err, payload: null }));
  }
}

type TaxExemptionIdentifier = { taxId: string | null; ssn: string | null };

function getTaxExemptionIdentifier(
  isTaxExempt: boolean,
  shouldUseTaxId: boolean,
  businessTaxId: string,
  businessSSN: string,
): TaxExemptionIdentifier {
  if (!isTaxExempt) {
    return { taxId: null, ssn: null };
  }

  return shouldUseTaxId ? { taxId: businessTaxId, ssn: null } : { taxId: null, ssn: businessSSN };
}

export function* createOrUpdateCustomerSaga(): Generator<
  PutEffect | SelectEffect | CallEffect,
  void,
  string & AddressType & boolean
> {
  yield put(appActions.setIsModalVisible(false));

  const phoneCountryCode: CountryCode = yield select(selectPhoneCountryCode);
  const partyId: string = yield select(selectPartyId);
  const serviceAddress: AddressType = yield select(selectServiceAddressValues);
  const billingAddress: AddressType = yield select(selectBillingAddressValues);
  const shippingAddress: AddressType = yield select(selectShippingAddressValues);
  const customerType: CustomerType = yield select(selectCustomerType);
  const organizationName = yield select(selectBusinessInformationName);
  const businessTaxId: string = yield select(selectBusinessInformationTaxID);
  const businessSSN: string = yield select(selectBusinessInformationSSN);
  const shouldUseTaxId: boolean = yield select(selectShouldUseTaxId);
  const organizationPartyId = yield select(selectOrganizationPartyId);
  const isOrganization = customerType === CustomerType.B;
  const country = yield select(selectCountry);
  const isVermontCustomer: boolean = yield select(selectIsVermontCustomer);
  const isTaxExempt: boolean = yield select(selectIsTaxExempt);

  const customerInfo: CustomerInfo = !isOrganization
    ? yield select(selectCustomerInformation)
    : yield select(selectAccountHolderInformation);

  const taxIdValue = shouldUseTaxId ? businessTaxId : businessSSN;

  if (customerInfo.phoneNumber) {
    const parsedPhoneNumber = parsePhoneNumberFromString(customerInfo.phoneNumber, phoneCountryCode);
    if (parsedPhoneNumber) {
      customerInfo.phoneNumber = parsedPhoneNumber.format('E.164');
    }
  }

  if (customerInfo.mobileNumber) {
    const parsedMobileNumber = parsePhoneNumberFromString(customerInfo.mobileNumber, phoneCountryCode);
    if (parsedMobileNumber) {
      customerInfo.mobileNumber = parsedMobileNumber.format('E.164');
    }
  }

  // Send the exact addressLines scrubAddress returned for every address (apt/unit intact).
  const unsanitizedServiceAddressLines: string[] | null = yield select(selectUnsanitizedAddressLines);
  const unsanitizedBillingAddressLines: string[] | null = yield select(selectUnsanitizedBillingAddressLines);
  const unsanitizedShippingAddressLines: string[] | null = yield select(selectUnsanitizedShippingAddressLines);
  const customerAddresses = {
    serviceAddress: applyUnsanitizedAddressLines(serviceAddress, unsanitizedServiceAddressLines),
    billingAddress: areAddressesEqual(billingAddress, serviceAddress)
      ? undefined
      : applyUnsanitizedAddressLines(billingAddress, unsanitizedBillingAddressLines),
    shippingAddress: areAddressesEqual(shippingAddress, serviceAddress)
      ? undefined
      : applyUnsanitizedAddressLines(shippingAddress, unsanitizedShippingAddressLines),
  };

  let createCustomerResponse: CreateCustomerPartyAndRelnId;

  if (partyId) {
    if (isOrganization) {
      if (USE_NEW_API) {
        const planProductFamily: string = yield select(selectProductFamily);
        const hasFiberPlan = isFiber(planProductFamily);

        try {
          yield put(personalInformationActions.setIsPersonalInformationLoading(true));
          yield call(updateOrganizationRest, {
            individualPartyId: partyId,
            organizationPartyId: organizationPartyId,
            organizationName: organizationName,
            ...customerInfo,
            ...customerAddresses,
            ...getTaxExemptionIdentifier(isTaxExempt, shouldUseTaxId, businessTaxId, businessSSN),
            customerType,
            country,
          });

          if (hasFiberPlan) {
            yield put(personalInformationActions.sendFiberRetargetingData());
          }
          yield put(personalInformationActions.sendGenericRetargetingData());
          yield put(personalInformationActions.setMarketPreferences());
          yield put(
            personalInformationActions.navigationGenerator({ billingAddress, serviceAddress, shippingAddress }),
          );
        } catch (err: any) {
          yield put(
            personalInformationActions.onSagaError({ callingSaga: 'createOrUpdateCustomerSaga', err, payload: null }),
          );
        }
      } else {
        yield put(
          personalInformationActions.updateOrganization({
            individualPartyId: partyId,
            organizationPartyId: organizationPartyId,
            organizationName: organizationName,
            ...customerInfo,
            ...customerAddresses,
            taxId: taxIdValue,
            isTaxIdSsn: !shouldUseTaxId,
            isTaxExempt,
            customerType,
            country,
          }),
        );
      }
    } else {
      yield put(
        personalInformationActions.updateCustomer({
          partyId,
          ...customerInfo,
          ...customerAddresses,
          customerType,
          country,
        }),
      );
    }
  } else {
    if (isOrganization) {
      createCustomerResponse = yield call(createOrganizationSaga, {
        payload: {
          ...customerInfo,
          ...customerAddresses,
          organizationName: organizationName,
          taxId: taxIdValue,
          isTaxIdSsn: !shouldUseTaxId,
          isTaxExempt,
          customerType,
        },
        type: personalInformationActions.createOrganization.type,
      });
    } else {
      createCustomerResponse = yield call(createCustomerSaga, {
        payload: {
          ...customerInfo,
          ...customerAddresses,
          customerType,
        },
        type: personalInformationActions.createCustomer.type,
      });
    }
    yield put(appActions.setPartyId(createCustomerResponse.partyId));
    yield put(appActions.setRelnId(createCustomerResponse.relnId));

    if (!USE_NEW_API && (isOrganization || isVermontCustomer)) {
      yield put(paymentInformationActions.getTaxCodes());
    }
  }
}

function* onEditPostalCodeClickSaga(): Generator {
  yield put(
    appActions.analyticsEvent({
      category: TrackingCategory.NAV,
      action: TrackingActions.CLICKED,
      params: { label: TrackingLabel.UPDATE_ZIPCODE },
    }),
  );
  yield put(appActions.setIsCartVisible(false));
  yield put(personalInformationActions.setIsPersonalInformationLoading(false));
  yield put(navActions.routeUserTo(Routes.SERVICEABILITY));
}

function* onErrorSaga(action: PayloadAction<ErrorPayload>): Generator {
  clientLogger(action.payload.callingSaga, action.payload.err);
  yield put(
    appActions.analyticsEvent({
      category: TrackingCategory.PERSONAL_INFORMATION,
      action: TrackingActions.ERROR,
      params: undefined,
    }),
  );
  yield put(personalInformationActions.setIsPersonalInformationLoading(false));
  yield put(appActions.setModalVisible(Modals.ERROR));
}

export function* validateAndCreateOrUpdateCustomer() {
  const formValid: boolean = yield select(selectPersonalInformationValidation);

  if (formValid) {
    const billingAddress: AddressFieldStateType = yield select(selectBillingAddress);
    const shippingAddress: AddressFieldStateType = yield select(selectShippingAddress);

    yield put(addressActions.setPreviousBillingAddress(billingAddress));
    yield put(addressActions.setPreviousShippingAddress(shippingAddress));
    yield call(createOrUpdateCustomerSaga);
  } else {
    yield put(personalInformationActions.setIsPersonalInformationLoading(false));
  }
}

export function* showConfirmBillingAddressModal() {
  yield put(personalInformationActions.setIsPersonalInformationLoading(false));
  yield put(appActions.setModalVisible(Modals.CONFIRM_BILLING_ADDRESS));
}

export function* setBillingAddressValid() {
  const country: Country = yield select(selectCountry);
  yield put(addressActions.setBillingAddressLinesValid());
  yield put(addressActions.setBillingZipcodeValid(country));
  yield put(addressActions.setBillingMunicipalityValid());
  yield put(addressActions.setBillingRegionValid());
}

export function* setShippingAddressValid() {
  const country: Country = yield select(selectCountry);
  yield put(addressActions.setShippingAddressLinesValid());
  yield put(addressActions.setShippingZipcodeValid(country));
  yield put(addressActions.setShippingMunicipalityValid());
  yield put(addressActions.setShippingRegionValid());
}

export function* scrubBillingAddress() {
  const address: AddressType = yield select(selectBillingAddressValues);
  const prevBillingAddress: AddressType = yield select(selectPreviousBillingAddressValues);

  const billingAddressChanged = hasAddressBeenChanged(address, prevBillingAddress);
  if (!billingAddressChanged) {
    yield call(validateShippingAddress);
    return;
  }

  try {
    yield put(personalInformationActions.setIsPersonalInformationLoading(true));
    const {
      data: { scrubAddress },
    } = yield call<any>(scrubAddressAsync, {
      address,
    });
    yield put(
      addressActions.setScrubBillingAddress({
        address: scrubAddress,
        coordinates: { latitude: scrubAddress.latitude, longitude: scrubAddress.longitude },
      }),
    );
    // Keep the exact addressLines scrubAddress returned so downstream providers send the same value the backend gave us.
    yield put(addressActions.setUnsanitizedBillingAddressLines(scrubAddress.addressLines));

    yield put(addressActions.setBillingAddress(address));
    if (scrubAddress.invalidAddress) {
      yield put(appActions.setModalVisible(Modals.BILLING_ADDRESS_NOT_VERIFIED));
      yield put(personalInformationActions.setIsPersonalInformationLoading(false));
      yield put(addressActions.setBillingAddress(scrubAddress));
    } else if (scrubAddress.processStatus === ProcessStatus.C) {
      yield call(showConfirmBillingAddressModal);
    } else if (scrubAddress.processStatus === ProcessStatus.V) {
      yield put(addressActions.setBillingAddress(scrubAddress));
      yield call(setBillingAddressValid);
      yield call(validateShippingAddress);
    } else {
      yield put(personalInformationActions.setIsPersonalInformationLoading(false));
      yield put(appActions.setModalVisible(Modals.CONFIRM_BILLING_ADDRESS));
    }
  } catch (err: any) {
    yield put(personalInformationActions.setIsPersonalInformationLoading(false));
    clientLogger('scrubBillingAddress: ', err);
    yield put(
      appActions.analyticsEvent({
        category: TrackingCategory.BILLING_ADDRESS_CHECK,
        action: TrackingActions.ERROR,
        params: undefined,
      }),
    );
    yield put(
      appActions.setErrorRetryAction({ type: personalInformationActions.scrubBillingAddress.type, payload: null }),
    );
    yield put(appActions.setModalVisible(Modals.ERROR));
  }
}

export function* scrubShippingAddress() {
  const address: AddressType = yield select(selectShippingAddressValues);
  const prevShippingAddress: AddressType = yield select(selectPreviousShippingAddressValues);

  const shippingAddressChanged = hasAddressBeenChanged(address, prevShippingAddress);
  if (!shippingAddressChanged) {
    yield call(validateAndCreateOrUpdateCustomer);
    return;
  }

  try {
    yield put(personalInformationActions.setIsPersonalInformationLoading(true));
    const {
      data: { scrubAddress },
    } = yield call<any>(scrubAddressAsync, {
      address,
    });
    const scrubbedAddress = {
      addressLines: [scrubAddress.addressLines[0], scrubAddress.addressLines[1] ?? ''],
      region: scrubAddress.region,
      municipality: scrubAddress.municipality,
      postalCode: scrubAddress.postalCode,
      countryCode: scrubAddress.countryCode,
    };
    yield put(
      addressActions.setScrubShippingAddress({
        address: scrubbedAddress,
        coordinates: { latitude: scrubAddress.latitude, longitude: scrubAddress.longitude },
      }),
    );
    // Keep the exact addressLines scrubAddress returned so downstream providers send the same value the backend gave us.
    yield put(addressActions.setUnsanitizedShippingAddressLines(scrubAddress.addressLines));
    yield put(addressActions.setShippingAddress(address));
    if (scrubAddress.invalidAddress) {
      yield put(appActions.setModalVisible(Modals.SHIPPING_ADDRESS_NOT_VERIFIED));
      yield put(personalInformationActions.setIsPersonalInformationLoading(false));
    } else if (scrubAddress.processStatus === ProcessStatus.C) {
      yield put(personalInformationActions.setIsPersonalInformationLoading(false));
      yield put(appActions.setModalVisible(Modals.CONFIRM_SHIPPING_ADDRESS));
    } else if (scrubAddress.processStatus === ProcessStatus.V) {
      yield put(addressActions.setShippingAddress(scrubbedAddress));
      yield call(setShippingAddressValid);
      yield call(validateAndCreateOrUpdateCustomer);
    } else {
      yield call(showConfirmBillingAddressModal);
    }
  } catch (err: any) {
    yield put(personalInformationActions.setIsPersonalInformationLoading(false));
    clientLogger('scrubShippingAddress: ', err);
    yield put(
      appActions.analyticsEvent({
        category: TrackingCategory.SHIPPING_ADDRESS_CHECK,
        action: TrackingActions.ERROR,
        params: undefined,
      }),
    );
    yield put(
      appActions.setErrorRetryAction({ type: personalInformationActions.scrubShippingAddress.type, payload: null }),
    );
    yield put(appActions.setModalVisible(Modals.ERROR));
  }
}

export function* validateBillingAddress() {
  const isBillingEqualServiceAddress: boolean = yield select(selectBillingEqualsServiceAddress);

  if (isBillingEqualServiceAddress) {
    const serviceAddress: AddressType = yield select(selectServiceAddressValues);
    yield put(addressActions.setBillingAddress(serviceAddress));
    yield put(navActions.setupAppRoutes());
    yield call(setBillingAddressValid);
    yield call(validateShippingAddress);
  } else {
    const country: Country = yield select(selectCountry);
    yield put(navActions.setupAppRoutes());
    yield put(addressActions.validateBillingAddress(country));
    yield put(addressActions.validateShippingAddress(country));
    const isBillingAddressValid: boolean = yield select(selectIsBillingAddressValid);
    const isShippingAddressValid: boolean = yield select(selectIsShippingAddressValid);
    const isShippingEqualServiceAddress: boolean = yield select(selectShippingEqualsServiceAddress);
    if ((isBillingAddressValid && isShippingAddressValid) || (isBillingAddressValid && isShippingEqualServiceAddress)) {
      yield put(personalInformationActions.scrubBillingAddress());
    } else {
      yield put(personalInformationActions.setIsPersonalInformationLoading(false));
    }
  }
}

export function* validateShippingAddress() {
  const isShippingEqualServiceAddress: boolean = yield select(selectShippingEqualsServiceAddress);

  if (isShippingEqualServiceAddress) {
    const serviceAddress: AddressType = yield select(selectServiceAddressValues);
    yield put(addressActions.setShippingAddress(serviceAddress));
    yield put(navActions.setupAppRoutes());
    yield call(setShippingAddressValid);
    yield call(validateAndCreateOrUpdateCustomer);
  } else {
    const country: Country = yield select(selectCountry);
    yield put(navActions.setupAppRoutes());
    yield put(addressActions.validateShippingAddress(country));
    yield put(addressActions.validateBillingAddress(country));
    const isShippingAddressValid: boolean = yield select(selectIsShippingAddressValid);
    const isBillingAddressValid: boolean = yield select(selectIsBillingAddressValid);
    const isBillingEqualServiceAddress: boolean = yield select(selectBillingEqualsServiceAddress);
    if ((isShippingAddressValid && isBillingAddressValid) || (isShippingAddressValid && isBillingEqualServiceAddress)) {
      yield put(personalInformationActions.scrubShippingAddress());
    } else {
      yield put(personalInformationActions.setIsPersonalInformationLoading(false));
    }
  }
}

export function* updateMarketPreferencesSaga() {
  try {
    const relnId: string = yield select(selectRelnId);

    yield call(updateMarketingPreferencesAsync, {
      relnId,
      hasCustomerOptedIn: true,
    });
  } catch (err: any) {
    yield put(
      personalInformationActions.onSagaError({ callingSaga: 'updateMarketPreferencesSaga', err, payload: null }),
    );
  }
}

export function* sendFiberRetargetingDataSaga(): Generator<
  CallEffect | PutEffect | SelectEffect,
  void,
  string & Product[]
> {
  const estimatedServiceableDate: string = yield select(selectEstimatedServiceableDate);
  const customerRelationshipId: string = yield select(selectRelnId);
  const availableProducts: Product[] = yield select(selectAvailableProducts);
  const addressId = availableProducts.find((product) => product.characteristics?.productFamily === ProductFamily.FIBER)
    ?.characteristics.addressId;

  if (!customerRelationshipId) {
    yield put(
      personalInformationActions.onSagaError({
        callingSaga: 'sendFiberRetargetingDataSaga',
        err: 'Customer Relationship ID is required when user is not on fiber waitlist',
        payload: null,
      }),
    );
  }

  const fiberRetargetingInput = {
    recordType: MarketoRecordTypes.FIBER,
    fiberWaitlist: false,
    goLiveForecastDate: estimatedServiceableDate ? estimatedServiceableDate : '',
    addressId,
    customerRelationshipId: customerRelationshipId ? customerRelationshipId : '',
  };

  try {
    yield call<any>(sendFiberRetargetingDataAsync, fiberRetargetingInput);
  } catch (err) {
    yield put(
      personalInformationActions.onSagaError({
        callingSaga: 'sendFiberRetargetingDataSaga',
        err: (err as Error).message,
        payload: null,
      }),
    );
  }
}

export function* sendGenericRetargetingDataSaga() {
  const serviceAddress: AddressType = yield select(selectServiceAddressValues);
  const unsanitizedServiceAddressLines: string[] | null = yield select(selectUnsanitizedAddressLines);
  const planName: string = yield select(selectPlanName);
  const planPrice: number = yield select(selectMonthlyTotalWithPromo);
  const availableProducts: Product[] = yield select(selectAvailableProducts);
  const availableProductNames = availableProducts.map((product) => product.name);
  const customerRelationshipId: string = yield select(selectRelnId);
  const planProductFamily: string = yield select(selectProductFamily);

  const retargetingInput = {
    selectedPlan: planName,
    planPrice: String(planPrice),
    address: applyUnsanitizedAddressLines(serviceAddress, unsanitizedServiceAddressLines),
    viewedPlans: availableProductNames,
    customerRelationshipId,
    recordType: planProductFamily,
  };

  try {
    yield call<any>(sendGenericRetargetingDataAsync, retargetingInput);
  } catch (err) {
    yield put(
      personalInformationActions.onSagaError({
        callingSaga: 'sendGenericRetargetingDataSaga',
        err: (err as Error).message,
        payload: null,
      }),
    );
  }
}

export function* personalInformationSaga(): Generator {
  yield takeLatest(personalInformationActions.navigationGenerator.type, navigationGenerator);
  yield takeLatest(personalInformationActions.onEditPostalCodeClick.type, benchmark(onEditPostalCodeClickSaga));
  yield takeLatest(
    personalInformationActions.updateCustomer.type,
    benchmark<CreateIndividualInput & PartyId, CallEffect | PutEffect | SelectEffect, void, UpdateIndividualPayload>(
      updateCustomerSaga,
    ),
  );
  yield takeLatest(
    personalInformationActions.updateOrganization.type,
    benchmark<UpdateOrganizationInput, CallEffect | PutEffect | SelectEffect, void, UpdateOrganizationPayload>(
      updateOrganizationSaga,
    ),
  );
  yield takeLatest(
    personalInformationActions.createCustomer.type,
    benchmark<
      CreateIndividualInput,
      CallEffect | PutEffect | SelectEffect,
      CreateCustomerPartyAndRelnId,
      CreateIndividualResponse & FeatureFlags & string
    >(createCustomerSaga),
  );
  yield takeLatest(
    personalInformationActions.createOrganization.type,
    benchmark<
      CreateOrganizationInput,
      CallEffect | PutEffect | SelectEffect,
      CreateCustomerPartyAndRelnId,
      CreateOrganizationResponse & FeatureFlags & string
    >(createOrganizationSaga),
  );
  yield takeLatest(personalInformationActions.validateShippingAddress.type, validateShippingAddress);
  yield takeLatest(personalInformationActions.validateBillingAddress.type, validateBillingAddress);
  yield takeLatest(
    personalInformationActions.validateAndCreateOrUpdateCustomer.type,
    validateAndCreateOrUpdateCustomer,
  );
  yield takeLatest(personalInformationActions.scrubBillingAddress.type, benchmark(scrubBillingAddress));
  yield takeLatest(personalInformationActions.scrubShippingAddress.type, benchmark(scrubShippingAddress));
  yield takeLatest(personalInformationActions.createOrUpdateCustomer.type, createOrUpdateCustomerSaga);
  yield takeLatest(personalInformationActions.onSagaError, benchmark<ErrorPayload>(onErrorSaga));
  yield takeLatest(personalInformationActions.setMarketPreferences.type, updateMarketPreferencesSaga);
  yield takeLatest(personalInformationActions.sendGenericRetargetingData.type, sendGenericRetargetingDataSaga);
  yield takeLatest(personalInformationActions.sendFiberRetargetingData.type, sendFiberRetargetingDataSaga);
}
